/**
 * Hook to enforce correct forecast data processing order
 *
 * Ensures data flows through: Filter → Extend Reported → Merge → Cumulative
 * This prevents accidentally calling hooks in the wrong order.
 */

import type {
  UnifiedForecastResult,
  RebatePoint,
  RebateBreakdown,
} from '@erp/shared-types';
import { useFilteredForecastData } from './useFilteredForecastData';
import { useExtendedReportedForecast } from './useExtendedReportedForecast';
import { useSmartMergedForecastData } from './useSmartMergedForecastData';
import { useCumulativeForecastData } from './useCumulativeForecastData';

interface UseForecastDataPipelineParams {
  /** Raw forecast data from API */
  rawData: UnifiedForecastResult | null | undefined;
  /** Filter functions to apply */
  filterFns: {
    rule?: (bd: RebateBreakdown) => boolean;
    division?: (bd: RebateBreakdown) => boolean;
  };
  /** Whether to convert to cumulative format */
  isCumulative: boolean;
  /** Current date for date-based calculations (defaults to now) */
  currentDate?: Date;
  /** Optional: Last real estimate for prediction splitting */
  lastRealEstimate?: RebatePoint | null;
  /** Optional: Day to split past/future predictions */
  predictionSplitDay?: number | null;
}

interface UseForecastDataPipelineReturn {
  /** Filtered data (after applying filters) */
  filtered: UnifiedForecastResult | undefined;
  /** Extended data (after extending reported with future predictions) */
  extended: UnifiedForecastResult | undefined;
  /** Merged data (after merging estimates and predictions) */
  merged: UnifiedForecastResult | undefined;
  /** Transformed data (after cumulative conversion if enabled) */
  transformed: UnifiedForecastResult | undefined;
}

/**
 * Hook to process forecast data through the correct pipeline
 *
 * Enforces order: Filter → Extend Reported → Merge → Cumulative
 * Returns all intermediate stages for debugging/flexibility.
 */
export function useForecastDataPipeline({
  rawData,
  filterFns,
  isCumulative,
  currentDate,
  lastRealEstimate = null,
  predictionSplitDay = null,
}: UseForecastDataPipelineParams): UseForecastDataPipelineReturn {
  // Step 1: Apply filters
  const filtered = useFilteredForecastData({
    forecastData: rawData || undefined,
    filterFns,
  });

  // Step 2: Extend reported line with future points from linear-regression predictions
  // This happens after filtering but before cumulative transformation
  const extendedResult = useExtendedReportedForecast(filtered);
  const extended = extendedResult.forecastData ?? undefined;

  // Step 3: Merge estimates and predictions based on date logic
  // This must happen BEFORE cumulative transformation to ensure correct totals
  const mergedResult = useSmartMergedForecastData(extended, currentDate);
  const merged =
    (mergedResult as UnifiedForecastResult | null | undefined) || undefined;

  // Step 4: Transform to cumulative format if needed
  const transformed = useCumulativeForecastData(
    merged,
    isCumulative,
    lastRealEstimate,
    predictionSplitDay,
  );

  return {
    filtered: filtered || undefined,
    extended: extended || undefined,
    merged,
    transformed: transformed || undefined,
  };
}
